/**
 * 放送地点一覧テーブル。
 *
 * - 行クリックまたは「編集」ボタンで編集モーダル (BroadcastPointForm) を開く。
 * - 「複製」ボタン (`onCopy` 指定時のみ) で、その地点の内容を引き継いだ新規作成を開始する。
 *   コピー先の線区/方面の選択は親側 (CopyDialog) が担当する。
 * - 「削除」は確認ダイアログを挟んで API DELETE を実行する。
 * - 一括削除用のチェックボックス列は、`selectedKeys` / `onToggleSelect` を渡したときのみ表示する
 *   (Issue #628)。ヘッダのチェックボックスで表示中の全行を一括選択 / 解除できる。
 * - 「有効」列のトグルで放送地点ごとに放送の有効 / 無効を切り替える (Issue #679)。
 *   `onToggleEnabled` 指定時のみ操作可能なトグルを表示し、未指定時は状態の表示のみ。
 *   無効の地点は行をグレーアウトして一目で区別できるようにする。
 * - spotId はバックエンド管理項目のため、テーブルには表示しない。
 * - コンテンツは件数ではなく、設定済みの中身 (カテゴリ + 主要プロパティ) を一覧表示し、
 *   「何がどう設定されているか」を一目で確認できるようにする。
 */
import type {
  BroadcastContent,
  BroadcastPoint,
  ConnectingLineDelayByStationProperties,
  ConnectingLineDelayProperties,
  FreeTextProperties,
  SelfTrainDelayProperties,
  TrainInfoProperties,
} from '../api/types';
import { extractContents, getCategoryMeta, isEmptySsml } from '../utils/broadcastContents';
import { broadcastSpotTarget } from '../pages/masterHistoryTarget';

import { MasterHistoryLink } from './MasterHistoryLink';

/**
 * 放送地点の選択キー。`spotId` だけでは線区#方面をまたいで一意と限らないため
 * `routeDirection` と組み合わせる。
 */
export function getPointKey(point: BroadcastPoint): string {
  return `${point.routeDirection}\u0000${point.spotId}`;
}

interface BroadcastPointsTableProps {
  points: BroadcastPoint[];
  onEdit: (point: BroadcastPoint) => void;
  /**
   * 「複製」ボタン押下時に呼ばれる。省略時は複製ボタン自体を表示しない。
   * 編集と削除の間に表示する。
   */
  onCopy?: (point: BroadcastPoint) => void;
  onDelete: (point: BroadcastPoint) => void;
  /** 行をクリックした時に呼ばれる (地図フォーカス用)。任意。 */
  onRowClick?: (point: BroadcastPoint) => void;
  /** 選択中の放送地点キー (`getPointKey`)。指定するとチェックボックス列を表示する。 */
  selectedKeys?: ReadonlySet<string>;
  /** 行のチェックボックス切替時に呼ばれる。 */
  onToggleSelect?: (point: BroadcastPoint) => void;
  /** ヘッダのチェックボックス操作時に呼ばれる (checked=true で表示中の全行を選択)。 */
  onToggleSelectAll?: (checked: boolean) => void;
  /** 一括削除の実行中など、選択操作を無効化するか。 */
  selectionDisabled?: boolean;
  /**
   * 「有効」トグル切替時に呼ばれる (Issue #679)。`enabled` は切り替え後の値。
   * 省略時はトグルを操作不可 (disabled) で表示する。
   */
  onToggleEnabled?: (point: BroadcastPoint, enabled: boolean) => void;
  /** 有効 / 無効の更新中 (`getPointKey`)。該当行のトグルだけ操作不可にする。 */
  togglingKeys?: ReadonlySet<string>;
}

/** 放送地点が有効か。`enabled` 未設定 (既存データ) は有効扱い。 */
function isPointEnabled(point: BroadcastPoint): boolean {
  return point.enabled !== false;
}

export function BroadcastPointsTable({
  points,
  onEdit,
  onCopy,
  onDelete,
  onRowClick,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  selectionDisabled = false,
  onToggleEnabled,
  togglingKeys,
}: BroadcastPointsTableProps): React.JSX.Element {
  const selectable = selectedKeys !== undefined && onToggleSelect !== undefined;
  const selectedCount = selectable
    ? points.filter((p) => selectedKeys.has(getPointKey(p))).length
    : 0;
  const allSelected = points.length > 0 && selectedCount === points.length;

  if (points.length === 0) {
    return (
      <div className="empty-state">
        該当する放送地点はありません。「＋ 新規作成」から作成してください。
      </div>
    );
  }

  return (
    <div className="table-wrapper">
      <table className="points-table">
        <thead>
          <tr>
            {selectable ? (
              <th className="select-cell">
                <input
                  type="checkbox"
                  data-testid="select-all-checkbox"
                  aria-label="表示中の放送地点をすべて選択"
                  checked={allSelected}
                  disabled={selectionDisabled || !onToggleSelectAll}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedCount > 0 && !allSelected;
                  }}
                  onChange={(e) => onToggleSelectAll?.(e.target.checked)}
                />
              </th>
            ) : null}
            <th>有効</th>
            <th>線区#方面</th>
            <th>説明</th>
            <th>半径(m)</th>
            <th>放送遅延(s)</th>
            <th>コンテンツ</th>
            <th aria-label="操作"></th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => {
            const contents = extractContents(p);
            const description = p.description ?? '';
            const enabled = isPointEnabled(p);
            return (
              <tr
                key={`${p.routeDirection}-${p.spotId}`}
                data-testid={`point-row-${p.spotId}`}
                data-description={description}
                data-enabled={enabled ? 'true' : 'false'}
                className={enabled ? undefined : 'point-row--disabled'}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
                onClick={onRowClick ? () => onRowClick(p) : undefined}
              >
                {selectable ? (
                  <td className="select-cell" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      data-testid={`select-checkbox-${p.spotId}`}
                      aria-label={`${description || '(名称未設定)'} を選択`}
                      checked={selectedKeys.has(getPointKey(p))}
                      disabled={selectionDisabled}
                      onChange={() => onToggleSelect(p)}
                    />
                  </td>
                ) : null}
                <td className="enabled-cell" onClick={(e) => e.stopPropagation()}>
                  <label className="enabled-toggle">
                    <input
                      type="checkbox"
                      role="switch"
                      data-testid={`enabled-toggle-${p.spotId}`}
                      aria-label={`${description || '(名称未設定)'} の放送を${enabled ? '無効' : '有効'}にする`}
                      checked={enabled}
                      disabled={!onToggleEnabled || (togglingKeys?.has(getPointKey(p)) ?? false)}
                      onChange={(e) => onToggleEnabled?.(p, e.target.checked)}
                    />
                    <span className="enabled-toggle__slider" aria-hidden="true"></span>
                    <span className="enabled-toggle__text">{enabled ? '有効' : '無効'}</span>
                  </label>
                </td>
                <td>
                  <span>{p.routeDirection}</span>
                </td>
                <td>{description}</td>
                <td>{formatNumber(p.radius_meters, 0)}</td>
                <td>{p.broadcast_delay_seconds ?? ''}</td>

                <td>
                  {contents.length === 0 ? (
                    <span className="contents-empty">（なし）</span>
                  ) : (
                    <ul className="contents-cell">
                      {contents.map((c, i) => (
                        <li key={i}>{summarizeContent(c)}</li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="row-actions">
                  <button
                    type="button"
                    data-testid={`edit-button-${p.spotId}`}
                    aria-label={`${description || '(名称未設定)'} を編集`}
                    onClick={() => onEdit(p)}
                  >
                    編集
                  </button>
                  {onCopy ? (
                    <button
                      type="button"
                      data-testid={`copy-button-${p.spotId}`}
                      aria-label={`${description || '(名称未設定)'} を複製`}
                      title="この放送地点の内容を引き継いで新規作成します"
                      onClick={() => onCopy(p)}
                    >
                      複製
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="danger"
                    data-testid={`delete-button-${p.spotId}`}
                    aria-label={`${description || '(名称未設定)'} を削除`}
                    onClick={() => onDelete(p)}
                  >
                    削除
                  </button>
                  <MasterHistoryLink
                    target={broadcastSpotTarget(p.routeDirection, p.spotId)}
                    subject={description || '(名称未設定)'}
                    testId={`history-link-${p.spotId}`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatNumber(value: number | undefined, fractionDigits = 6): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '';
  return value.toFixed(fractionDigits);
}

/**
 * コンテンツ 1 件を「カテゴリラベル + 主要プロパティ」の 1 行サマリ文字列にする。
 * 一覧テーブル上で中身を把握するための簡易表示。
 */
function summarizeContent(content: BroadcastContent): string {
  const meta = getCategoryMeta(content.category);
  const label = meta?.label ?? content.category;
  switch (content.category) {
    case 'self_train_delay': {
      const props = content.properties as Partial<SelfTrainDelayProperties>;
      const patternCount = Array.isArray(props.delay_patterns) ? props.delay_patterns.length : 0;
      return `${label}（${patternCount} パターン）`;
    }
    case 'connecting_line_delay': {
      const props = content.properties as Partial<ConnectingLineDelayProperties>;
      const routeId = props.route_id ? `route_id: ${props.route_id}` : 'route_id 未設定';
      const threshold =
        typeof props.delay_threshold_seconds === 'number'
          ? `しきい値 ${props.delay_threshold_seconds}秒`
          : 'しきい値 未設定';
      return `${label}（${routeId} / ${threshold}）`;
    }
    case 'connecting_line_delay_by_station': {
      // 接続線遅延 (乗換駅ベース / part2) は「乗換駅 (stop_id) / 接続線N件」の
      // 2 要素サマリで表示する。UI 実装上、乗換駅名までは事前展開しない
      // (Table 側で routes/routeStops マスタを持たないため) ので stop_id をそのまま出す。
      const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
      // レガシー: 旧「その他」モード (`transfer_stop_mode === 'other'`) で保存済みのデータは
      // 乗換駅を使わず運行情報のみを放送し続ける。乗換駅 stop_id を出しても意味が無い
      // (常に空) ので、「運行情報のみ」と表示して区別できるようにする。
      const transferStop =
        props.transfer_stop_mode === 'other'
          ? '運行情報のみ'
          : props.transfer_stop_id
            ? `乗換駅: ${props.transfer_stop_id}`
            : '乗換駅 未設定';

      const lineCount = Array.isArray(props.lines) ? props.lines.length : 0;
      return `${label}（${transferStop} / 接続線 ${lineCount}件）`;
    }
    case 'train_info': {
      // 運行情報のみ。参照元 (アイステイションズ路線の組) の件数を出す。
      const props = content.properties as Partial<TrainInfoProperties>;
      const lineCount = Array.isArray(props.lines) ? props.lines.length : 0;
      return `${label}（接続線 ${lineCount}件）`;
    }
    case 'free_text': {
      const props = content.properties as Partial<FreeTextProperties>;
      const mode = resolveFreeTextMode(props);
      return `${label}（${mode}）`;
    }
    default:
      return label;
  }
}

/**
 * 任意テキストコンテンツの入力モードを表示用ラベルに変換する。
 *
 * `input_mode` 未指定 (旧データ) の場合の推測順は編集画面
 * (`broadcastContents/logic.ts` の `resolveInputMode`) と揃える。
 * 中身のない SSML (旧初期値の `<speak></speak>` など) は「SSML 未設定」とみなし、
 * 通常テキスト扱いにフォールバックする。
 */
function resolveFreeTextMode(props: Partial<FreeTextProperties>): string {
  const mode =
    props.input_mode ?? (props.audio_key ? 'audio' : !isEmptySsml(props.ssml) ? 'ssml' : 'text');
  switch (mode) {
    case 'audio':
      return '音声ファイル';
    case 'ssml':
      return 'SSML';
    default:
      return 'テキスト';
  }
}
